import type { MessageId } from '@trust_network/shared/message-ids';

const errorMessages = {
  admin_only: 'Доступно только администраторам платформы.',
  approval_required: 'Сейчас это изменение нельзя применить автоматически. Нужен отдельный этап подтверждения.',
  cycle_detected: 'Так сделать нельзя: получилась бы замкнутая цепочка передачи.',
  delegation_target_ineligible:
    'Получатель делегирования должен иметь заполненный профиль (имя и подтверждённые контакты).',
  db_error: 'Ошибка базы данных. Попробуйте еще раз чуть позже.',
  internal_server_error: 'На сервере произошла ошибка. Мы не смогли завершить действие. Попробуйте еще раз чуть позже.',
  invalid_code: 'Код неверный или уже просрочен.',
  invalid_credentials: 'Неверный номер телефона или пароль.',
  invalid_token: 'Сессия недействительна. Войдите снова.',
  kyc_email_already_verified: 'Email уже подтверждён.',
  kyc_email_token_expired: 'Ссылка подтверждения email устарела. Запросите новую.',
  kyc_email_token_invalid: 'Ссылка подтверждения email недействительна.',
  kyc_email_send_failed: 'Не удалось отправить письмо подтверждения. Попробуйте ещё раз.',
  kyc_l2_too_many_attempts: 'Превышено количество попыток. Повторите попытку завтра.',
  kyc_l2_document_invalid: 'Документ не прошёл проверку. Убедитесь, что фото чёткое и паспорт виден полностью.',
  kyc_l3_too_many_attempts: 'Превышено количество попыток верификации лица. Повторите попытку завтра.',
  kyc_l3_face_match_failed: 'Лицо на видео не совпало с фото в паспорте. Повторите процедуру.',
  kyc_level_insufficient:
    'Сначала завершите предыдущий шаг подтверждения профиля.',
  llm_provider_unavailable: 'Консультант сейчас недоступен. Попробуйте позже.',
  llm_request_failed: 'Консультант не смог ответить. Попробуйте ещё раз.',
  missing_token: 'Нужна авторизация. Войдите снова.',
  phone_verification_failed: 'Не удалось завершить подтверждение номера. Попробуйте еще раз.',
  request_failed: 'Не удалось выполнить запрос. Попробуйте еще раз.',
  self_delegation_forbidden: 'Нельзя передать голос самому себе.',
  sms_send_failed: 'Не удалось отправить код подтверждения. Попробуйте еще раз.',
  telegram_chat_not_found: 'Чат Telegram не найден.',
  telegram_not_configured: 'Интеграция с Telegram не настроена.',
  telegram_send_failed: 'Не удалось отправить сообщение в Telegram. Попробуйте ещё раз.',
  unknown_error: 'Что-то пошло не так. Попробуйте еще раз.',
  user_not_found: 'Пользователь с таким ID не найден.',
  validation_code_invalid: 'Код должен состоять ровно из 6 символов.',
  validation_code_required: 'Введите код подтверждения.',
  validation_delegate_target_invalid: 'ID пользователя должен быть строкой.',
  validation_delegate_target_required: 'Введите ID пользователя.',
  validation_device_id_invalid: 'Некорректный идентификатор устройства.',
  validation_error: 'Проверьте, правильно ли заполнены поля.',
  validation_llm_message_required: 'Введите сообщение.',
  validation_llm_message_too_long: 'Сообщение слишком длинное.',
  validation_phone_invalid: 'Введите телефон в международном формате: + и 8-20 цифр без пробелов и дефисов.',
  validation_phone_required: 'Введите номер телефона.',
} satisfies Record<MessageId, string>;

export const messages = {
  ru: {
    app: {
      title: 'LiveNet',
      subtitle: 'Сеть решений',
      tagline: 'test',
      shortDescription: 'Объединяйся с теми, кому доверяешь',
      theme: {
        button: 'Тема',
        light: 'Светлая',
        dark: 'Темная',
        system: 'Системная',
      },
      actions: {
        install: 'Установить приложение',
      },
      profile: {
        open: 'Профиль и сессия',
        title: 'Мой профиль',
        name: 'Имя',
        guestName: 'Гость',
        unnamed: 'Безымянный',
        guestBadge: 'Гость',
        verifiedBadge: 'Подтвержден',
        phoneHint: 'Телефон подтвержден и привязан к этой сессии. Можно безопасно возвращаться и делиться своим ID.',
        authorize: 'Подтвердить телефон',
        login: 'Войти',
        edit: 'Редактировать',
        openProfileVerification: 'Подтвердить профиль',
        signIn: 'Зарегистрироваться',
        logout: 'Выйти',
        uploadAvatar: 'Загрузить аватар',
        uploadAvatarHint: 'Аватар помогает делать профиль более узнаваемым.',
      },
      navigation: {
        main: 'Главная',
        chain: 'Цепочка',
        initiatives: 'Инициативы',
      },
      connecting: 'Подключаем приложение...',
    },
    main: {
      stats: {
        currentRoot: 'Кому сейчас достается голос',
        chainLength: 'Сколько людей в цепочке',
        session: 'Тип входа',
      },
      platform: {
        sectionTitle: 'Результаты платформы',
        sectionSubtitle:
          'Сводка по участникам, собранным бюджетам и инициативам на платформе.',
        users: 'Участников',
        collectedBudgets: 'Собранные бюджеты',
        budgetValue: '{amount} ₽',
        activeInitiatives: 'Активных инициатив',
        closedInitiatives: 'Успешно закрытых инициатив',
        loading: 'Загружаем статистику…',
        error: 'Не удалось загрузить статистику',
        retry: 'Обновить',
      },
      mission: {
        title: 'Наша миссия', // TODO: replace with reviewed copy
        p1: 'Децентрократия — это платформа для делегирования политического представительства в цифровую эпоху. Мы верим, что каждый голос должен иметь значение, а доверие между людьми — стать основой коллективных решений.', // TODO: replace with reviewed copy
        p2: 'Мы создаём инструменты, которые позволяют гражданам самостоятельно выбирать, кому доверить свой голос: другу, эксперту или публичному лидеру. Цепочка доверия прозрачна, ревокабельна и принадлежит только вам.', // TODO: replace with reviewed copy
        p3: 'Подтверждение профиля — это не надзор, а защита. Поэтапная верификация гарантирует, что каждый участник — реальный человек с реальными намерениями, без утечки персональных данных третьим лицам.', // TODO: replace with reviewed copy
        p4: 'Мы строим открытую гражданскую инфраструктуру — не для партий и правительств, а для людей. Присоединяйтесь к нам и участвуйте в формировании будущего.', // TODO: replace with reviewed copy
        roadmapTitle: 'Дорожная карта',
        phase2026: {
          title: 'Запуск MVP и верификация сообщества',
          description:
            'Базовая делегация голоса, заполнение и верификация профиля, публичный доступ к платформе.', // TODO: replace with reviewed copy
        },
        phase2027: {
          title: 'Расширение функций делегирования',
          description: 'Делегация по тематикам, история цепочки, мобильное приложение.', // TODO: replace with reviewed copy
        },
        phase2028: {
          title: 'Интеграция с гражданскими институтами',
          description: 'Партнёрства с муниципалитетами и НКО, API для внешних голосований.', // TODO: replace with reviewed copy
        },
        phase2029: {
          title: 'Федеральный масштаб',
          description: 'Охват всех регионов, расширенная аналитика, публичный аудит цепочек.', // TODO: replace with reviewed copy
        },
        phase2030: {
          title: 'Международная экспансия',
          description: 'Запуск в странах СНГ и Европе, мультиязычная поддержка.', // TODO: replace with reviewed copy
        },
        phase2031: {
          title: 'Открытый стандарт делегации',
          description: 'Публикация открытого протокола делегирования голоса для глобального сообщества.', // TODO: replace with reviewed copy
        },
      },
      initiatives: {
        title: 'Маркетплейс инициатив',
        subtitleAuthorized: 'Следите за новыми инициативами, выбирайте популярные и начинайте участие сразу с главной страницы.',
        subtitleGuest: 'После входа появятся персональные виджеты новых и популярных инициатив.',
        newTitle: 'Новые инициативы',
        popularTitle: 'Популярные инициативы',
        marketplaceCta: 'Перейти на маркетплейс инициатив',
        loading: 'Загружаем инициативы...',
        error: 'Не удалось загрузить инициативы. Попробуйте еще раз.',
        retry: 'Повторить',
        emptyNew: 'Пока нет новых инициатив.',
        emptyPopular: 'Пока нет популярных инициатив.',
        carouselPrevious: 'Предыдущая инициатива',
        carouselNext: 'Следующая инициатива',
        popularitySummary: 'Поддержали: {supporters} · Вес: {weight}',
        authRequired: 'Чтобы видеть новые и популярные инициативы на главной, войдите в аккаунт.',
      },
      sessionKinds: {
        guest: 'Гостевой',
        phone: 'По телефону',
      },
      primaryActions: {
        delegate: {
          title: 'Доверить голос',
          subtitle: 'выбрать человека, который будет представлять твою позицию',
          tab: 'Доверить голос',
          details: 'Сейчас голос можно передать вручную по ID пользователя или через QR-код другого участника.',
        },
        accept: {
          title: 'Принять голос',
          subtitle: 'собрать вокруг себя людей и представлять их позицию',
          tab: 'QR-код',
          details: 'Покажите этот код другому человеку, чтобы он мог быстро передать голос вам.',
        },
      },
      authDialog: {
        subtitle: 'Чтобы создать связь доверия, завершите авторизацию по телефону.',
        tabs: {
          phone: 'Телефон',
          providers: 'Провайдеры',
          email: 'Email',
        },
        phoneField: 'Номер телефона',
        codeField: 'Код подтверждения',
        requestCode: 'Получить код',
        resend: 'Отправить код снова',
        resendIn: 'Повтор через {seconds} c',
        confirmCode: 'Подтвердить код',
        verifyAndContinue: 'Подтвердить и продолжить',
        changePassword: 'Сменить пароль',
        cancel: 'Закрыть',
        providersHint: 'OAuth-провайдеры появятся после того, как сервер начнет их поддерживать.',
        emailHint: 'Регистрация, вход по email и восстановление пароля пока не поддерживаются backend-частью. UI подготовлен заранее.',
        emailField: 'Email',
        passwordField: 'Пароль',
        login: 'Войти',
        register: 'Зарегистрироваться',
        restorePassword: 'Восстановить пароль',
      },
      delegate: {
        title: 'Передать голос',
        subtitle: 'Сейчас голос можно передать вручную по ID пользователя или быстро заполнить поле через QR-код.',
        currentRecipient: 'Сейчас голос уже передан',
        followersCount: 'Последователей',
        verifiedFollowersPercent: 'Верифицированных последователей',
        toggleScanner: 'Сканировать QR-код',
        closeScanner: 'Закрыть сканер',
        revoke: 'Отменить передачу',
        mockCamera: 'Наведите камеру на QR-код другого пользователя, чтобы автоматически подставить его ID в поле делегирования.',
        scannerHintIdle: 'Наведите камеру на QR-код другого пользователя, чтобы автоматически подставить его ID в поле делегирования.',
        scannerHintScanning: 'Сканируем QR-код. Держите камеру так, чтобы код целиком помещался в рамку.',
        scannerHintDetected: 'QR-код найден. Готовим подстановку ID делегата…',
        scannerInvalidPayload: 'QR-код не содержит корректный ID делегата. Используйте QR-код из приложения или введите ID вручную.',
        scannerUserNotFound: 'Пользователь из QR-кода не найден. Проверьте актуальность кода или введите ID вручную.',
        scannerUnsupported: 'На этом устройстве камера или QR-сканирование недоступны. Введите ID пользователя вручную.',
        scannerPermissionDenied: 'Без доступа к камере QR-сканирование недоступно. Разрешите камеру или введите ID вручную.',
        scannerError: 'Не удалось запустить сканер QR-кода. Попробуйте еще раз или введите ID вручную.',
        targetPlaceholder: 'ID пользователя',
        save: 'Передать голос',
        helper: 'Введите ID другого существующего пользователя и нажмите "Передать голос".',
        pendingCurrent: 'Текущее делегирование: {value}',
        pendingRequested: 'Запрошенное делегирование: {value}',
        pendingRevoke: 'отмена делегирования',
      },
      aggregate: {
        title: 'Мой QR-код',
        subtitle: 'Покажите этот код другому человеку, чтобы он мог быстро передать голос вам.',
        myId: 'Мой ID',
        referralHint:
          'Можно не только показать QR-код, но и отправить ссылку, которая сразу подставит ваш ID в форму делегирования на главной странице.',
        copyReferral: 'Скопировать реферальную ссылку',
        referralCopied: 'Реферальная ссылка скопирована. Её можно отправить другому человеку.',
        referralCopyError: 'Не удалось скопировать ссылку автоматически. Попробуйте еще раз или скопируйте адрес страницы вручную.',
        unavailable: 'Не удалось получить ваш ID',
        unavailableHint: 'Сессия загрузилась не полностью. Попробуйте переподключиться или обновить страницу.',
        retry: 'Переподключиться',
        authRequiredTitle: 'Чтобы показать QR-код, нужно войти',
        authRequiredHint: 'Сначала авторизуйтесь. После входа ваш QR-код появится автоматически.',
        authorize: 'Войти',
      },
      upgrade: {
        title: 'Привязать телефон',
        subtitle: 'Подтвердите номер кодом из сообщения, чтобы привязать телефон к аккаунту.',
        requestCode: 'Получить код',
        mockCode: 'Код подтверждения придет в сообщение.',
        verifyPlaceholder: 'Код из 6 символов',
        verifyPhone: 'Подтвердить номер',
      },
      details: {
        title: 'Моя сессия',
        subtitle: 'Здесь показано, кто вы в системе и подтвержден ли ваш аккаунт.',
        user: 'Мой ID',
        session: 'Сессия',
        verified: 'Аккаунт подтвержден',
        phone: 'Телефон',
      },
      pwa: {
        title: 'Приложение',
        subtitle: 'Проект работает как PWA: может устанавливаться, не перезагружается при переходах и умеет получать уведомления.',
        installStatus: 'Установка',
        installed: 'Уже установлено',
        readyToInstall: 'Готово к установке',
        installUnavailable: 'Установка сейчас недоступна',
        notificationStatus: 'Уведомления',
        notificationsGranted: 'Разрешены',
        notificationsDenied: 'Запрещены',
        notificationsUnsupported: 'Не поддерживаются',
        notificationsDefault: 'Еще не разрешены',
        enableNotifications: 'Разрешить уведомления',
        testNotification: 'Проверить уведомление',
      },
    },
    footer: {
      updated: 'Обновлено',
      linksTitle: 'Карта сайта',
      donateSectionTitle: 'Поддержка проекта',
      platformTitle: 'Платформа',
      contactsTitle: 'Контакты',
      contactsPending: 'Контактные каналы будут опубликованы позже.',
      platform: 'О платформе',
      about: 'О нас',
      faq: 'FAQ',
      privacy: 'Конфиденциальность',
      terms: 'Условия',
      support: 'Поддержка',
      blog: 'Блог',
      careers: 'Карьера',
      donate: 'Донатить',
      telegram: 'Telegram',
      whatsapp: 'WhatsApp',
      phone: 'Телефон',
      email: 'Email',
    },
    infoPages: {
      about: {
        eyebrow: 'О проекте',
        title: 'Что такое LiveNet',
        subtitle:
          'LiveNet исследует понятную модель делегирования голоса, где маршрут доверия и финальный получатель видны самому участнику.',
        summary:
          'Проект собирает вокруг себя не только интерфейс передачи голоса, но и практику прозрачных правил, обратной связи и аккуратного роста продукта без скрытых ручных сценариев.',
        pointOne: 'Мы хотим, чтобы механику делегирования можно было объяснить обычному человеку без политического и технического жаргона.',
        pointTwo:
          'Команда проверяет не только сами функции, но и понятность маршрутов, состояний сессии и причин, почему голос оказывается у конкретного получателя.',
        pointThree:
          'Сейчас проекту особенно важны люди, готовые читать, тестировать, задавать неудобные вопросы и помогать вычищать продуктовые шероховатости.',
        contactsTitle: 'Контакты и каналы связи',
        contactsBody: 'Ниже собраны текущие каналы, через которые можно написать команде, задать вопрос или предложить сотрудничество.',
        legalTitle: 'Правовая и организационная рамка',
        legalBody:
          'Сейчас LiveNet находится на стадии прототипа. Отдельные страницы с privacy и terms будут вынесены в самостоятельные документы, но уже сейчас для правовых и организационных вопросов можно писать через опубликованные контакты.',
        primaryAction: 'Как устроена платформа',
        secondaryAction: 'Кто в команде',
      },
      faq: {
        eyebrow: 'FAQ',
        title: 'Частые вопросы',
        subtitle:
          'Короткие ответы о делегировании, инициативах, профиле и взносах — по тому, как платформа работает сейчас.',
        summary:
          'LiveNet — прототип сети доверия: вы выбираете получателя делегирования, видите цепочку до корневого узла и участвуете в инициативах сообщества. Ниже — без обещаний «скоро появится», только актуальные сценарии.',
        questionsTitle: 'Вопросы и ответы',
        linksTitle: 'Куда перейти',
        links: {
          goals: 'Инициативы — каталог и поддержка',
          chain: 'Цепочка делегирования',
          donate: 'Поддержка и взносы',
          profile: 'Профиль и подтверждение',
          support: 'Обратная связь',
          about: 'О проекте',
        },
        profileStatus: {
          title: 'Статус профиля',
          subtitle: 'Три уровня участника',
          intro:
            'В интерфейсе отображается один из статусов: профиль не заполнен, заполнен или верифицирован. Статус влияет на доверие к узлу в цепочке и на то, может ли участник быть получателем делегирования.',
          levelOne:
            'Не заполнен — после регистрации, пока не указаны имя и не подтверждены обязательные контакты.',
          levelTwo:
            'Заполнен — имя и email указаны, почта подтверждена. Такой участник может быть получателем делегирования на главной.',
          levelThree:
            'Верифицирован — пройдены проверка документа и лица. Максимальный уровень доверия в цепочке.',
          impact:
            'Этапы проходятся в профиле. Бейдж статуса в цепочке и карточках ведёт на этот раздел.',
        },
        items: {
          one: {
            question: 'Что такое LiveNet?',
            answer:
              'Платформа для прозрачного делегирования доверия: вы выбираете, кому передать свой голос, видите всю цепочку до корневого получателя и можете отозвать выбор. Параллельно доступны инициативы, сообщества и взносы в общий и целевые фонды.',
          },
          two: {
            question: 'Что значит «передать голос»?',
            answer:
              'Вы назначаете другого участника получателем делегирования. Его голос в цепочке учитывает ваш выбор, но отозвать делегирование можете только вы. Замкнутые цепочки (когда получатель косвенно ссылается обратно на вас) система блокирует.',
          },
          three: {
            question: 'Нужно ли входить в аккаунт?',
            answer:
              'Главную, каталог инициатив и страницу взносов можно открыть без входа. Разовый взнос можно оформить анонимно — он не попадёт в историю профиля. Для истории платежей, ежемесячных планов, делегирования и редактирования профиля нужен вход по телефону.',
          },
          four: {
            question: 'Как выбрать получателя делегирования?',
            answer:
              'На главной: введите ID участника или отсканируйте его QR-код. Также работают ссылки-приглашения с параметром delegate. Назначить получателем можно только участника с заполненным профилем (имя и подтверждённые контакты).',
          },
          five: {
            question: 'Можно ли сменить или отозвать делегирование?',
            answer:
              'Да, на главной или в разделе «Цепочка». Если на вас уже делегировали другие участники, смена или отзыв может потребовать их подтверждения — приложение покажет запрос и даст подтвердить или отменить.',
          },
          six: {
            question: 'Что такое инициативы?',
            answer:
              'В разделе «Инициативы» — проекты сообщества: описание, задачи, поддержка. По каждой инициативе можно поддержать напрямую или делегировать решение одному участнику. Прямая поддержка и активное делегирование в одной инициативе одновременно недоступны.',
          },
          seven: {
            question: 'Как устроены взносы?',
            answer:
              'Сначала выберите формат на /donate, затем оплатите на /donate/pay (сейчас — тестовая seed-карта из нулей). Комиссия 10%; маршрутизация, история и снимок фонда — в профиле после входа. Анонимный разовый платёж в историю не попадает.',
          },
          eight: {
            question: 'Что видят другие участники?',
            answer:
              'Публично: ваш ID, аватар, статус профиля и агрегаты в цепочке (например, число последователей). Полный граф всей сети не отображается — только ваша цепочка и корневой получатель, чтобы интерфейс оставался обозримым.',
          },
        },
      },
      privacy: {
        eyebrow: 'Конфиденциальность',
        title: 'Как LiveNet обращается с данными',
        subtitle: 'Даже в прототипе важно заранее объяснять, какие данные используются, зачем они нужны и куда писать с вопросами.',
        summary:
          'Сейчас приложение хранит только тот минимум, который нужен для работы сессии, подтверждения телефона и делегирования голоса. По мере развития продукта политика будет расширяться вместе с фактическими сценариями обработки данных.',
        pointOne:
          'Мы используем технические идентификаторы сессии, данные аккаунта и подтвержденный номер только там, где это нужно для работы пользовательского сценария.',
        pointTwo:
          'Доступ к данным должен быть ограничен прикладной задачей: восстановить сессию, показать статус пользователя и корректно провести делегирование.',
        pointThree:
          'Если у вас есть вопросы о хранении, удалении или обработке данных, сейчас лучше всего писать команде через опубликованные контакты.',
        primaryAction: 'О проекте',
        secondaryAction: 'Связаться с командой',
      },
      terms: {
        eyebrow: 'Условия использования',
        title: 'На каких условиях работает LiveNet',
        subtitle: 'Пока проект развивается как прототип, важно заранее обозначить границы использования, ответственности и обратной связи.',
        summary:
          'Эта страница фиксирует базовые правила использования платформы, ожидания к поведению участников и каналы, через которые стоит задавать вопросы о спорных сценариях.',
        pointOne:
          'Пользователь отвечает за корректность действий в своей сессии и не должен использовать платформу для намеренного обхода правил делегирования.',
        pointTwo:
          'Команда может менять интерфейс, маршруты и отдельные сценарии продукта по мере развития прототипа, если это улучшает прозрачность и надежность сервиса.',
        pointThree:
          'Если какое-то поведение приложения кажется непонятным или спорным, правильный путь сейчас — не обходить его вручную, а сообщить команде через доступные контакты.',
        primaryAction: 'О платформе',
        secondaryAction: 'Получить поддержку',
      },
      support: {
        eyebrow: 'Поддержка',
        title: 'Как связаться с командой LiveNet',
        subtitle: 'Здесь можно оставить обратную связь, сообщить о проблеме или уточнить, как сейчас работает тот или иной сценарий.',
        summary:
          'Форма пока не отправляет данные в backend, но уже помогает собрать понятный шаблон обращения. Для срочной связи ниже остаются прямые каналы контакта.',
        formTitle: 'Форма обратной связи',
        formError: 'Заполните имя, email и описание вопроса, чтобы форма была полезной.',
        formSuccess:
          'Сообщение локально подготовлено. Следующим шагом можно будет подключить реальную отправку на backend или внешний helpdesk.',
        nameLabel: 'Как к вам обращаться',
        emailLabel: 'Email для ответа',
        messageLabel: 'Ваш вопрос или проблема',
        submitLabel: 'Подготовить обращение',
        contactsTitle: 'Прямые каналы связи',
        contactsBody: 'Если вопрос не хочется держать только в форме, используйте один из опубликованных контактов ниже.',
      },
      blog: {
        eyebrow: 'Блог',
        title: 'Обновления и заметки LiveNet',
        subtitle:
          'Здесь будут собираться новости о развитии продукта, ключевых решениях команды и заметках о том, что меняется в интерфейсе.',
        summary:
          'Пока блог работает как контентная заготовка: он задает формат будущих публикаций и место, где пользователю будет проще следить за эволюцией продукта.',
        posts: {
          one: {
            title: 'Что мы считаем хорошим изменением продукта',
            subtitle: 'Заметка о прозрачности сценариев и понятных контрактах',
            body: 'LiveNet не пытается расти через хаотичное добавление функций. Для нас важнее, чтобы каждый новый сценарий было можно объяснить, протестировать и удержать без ручных исключений.',
          },
          two: {
            title: 'Почему интерфейс пока нарочно небольшой',
            subtitle: 'О фокусе на делегировании, а не на декоративной сложности',
            body: 'Сейчас проект сознательно оставляет только те элементы, которые помогают понять передачу голоса, состояние сессии и итогового получателя без визуального шума.',
          },
          three: {
            title: 'Что появится дальше',
            subtitle: 'О страницах, сценариях поддержки и развитии продукта',
            body: 'Следующие итерации будут добавлять не просто новые маршруты, а более полные информационные разделы, упрощение делегирования и более понятные точки входа для пользователя.',
          },
        },
        subscriptionTitle: 'Подписка на обновления',
        subscriptionBody:
          'Здесь позже появится форма или интеграция для подписки на новости проекта. Пока эта секция обозначает место для будущего канала уведомлений.',
      },
      careers: {
        eyebrow: 'Карьера',
        title: 'Как присоединиться к развитию LiveNet',
        subtitle:
          'Сейчас команда небольшая, поэтому нам особенно важны люди, которым близки продуктовая ясность, инженерная дисциплина и спокойное развитие сложных сценариев.',
        summary:
          'Страница пока собирает hiring-контур в placeholder-формате: какие роли могут понадобиться, что мы считаем сильным вкладом и как лучше выйти на связь.',
        vacanciesTitle: 'Какие роли могут появиться',
        vacancies: {
          one: {
            title: 'Frontend / product engineer',
            body: 'Нужен человек, который умеет упрощать интерфейс, не теряя точность сценариев, и доводить визуальные решения до реально удобного состояния.',
          },
          two: {
            title: 'Backend / platform engineer',
            body: 'Интересен опыт в контрактном проектировании, устойчивых доменных правилах и спокойной работе с техническим долгом без хаотичных обходов.',
          },
          three: {
            title: 'Product writer / researcher',
            body: 'Нужен человек, который умеет превращать сложную механику в понятные тексты, вопросы к пользователю и внятные продуктовые гипотезы.',
          },
        },
        contactTitle: 'Как откликнуться',
        contactBody:
          'Если вам близка идея проекта, сейчас лучший способ отклика — написать команде через публичные контакты и коротко рассказать, чем именно вы хотите быть полезны.',
      },
      donate: {
        eyebrow: 'Поддержка проекта',
        title: 'Тарифы поддержки LiveNet',
        subtitle:
          'Выберите формат взноса — все варианты на одном экране, с прозрачной маршрутизацией средств после комиссии 10%.',
        summary:
          'Финансовая помощь идет не в абстрактный баннер, а в развитие понятных сценариев делегирования, публичной отчетности и устойчивой технической базы проекта.',
        pricingTitle: 'Форматы поддержки',
        pricingSubtitle:
          'Три понятных уровня: развитие платформы, общий фонд инициатив или целевой взнос в конкретную инициативу.',
        popularBadge: 'Популярный',
        tierAmount: '{amount} ₽',
        optionsTitle: 'Оформление взноса',
        checkoutHint: 'Сумму и периодичность можно изменить перед подтверждением.',
        publicHint:
          'Страница доступна без входа. История платежей, маршрутизация и снимок распределения фонда — в профиле после авторизации.',
        amountLabel: 'Сумма',
        amountHelper: 'Комиссия платформы 10%. Подробная разбивка — в профиле.',
        frequencyLabel: 'Частота',
        frequencyOneTime: 'Разовый',
        frequencyMonthly: 'Ежемесячный',
        goalIdLabel: 'ID инициативы',
        goalIdHelper: 'Целевой взнос: 60% net в бюджет инициативы, 40% — в общий фонд',
        checkoutNext: 'Далее: подтверждение',
        checkoutBack: 'Назад',
        checkoutConfirm: 'Подтвердить платёж',
        checkoutPayAnonymous: 'Оплатить анонимно',
        checkoutSignInForHistory: 'Войти для сохранения в истории',
        checkoutAnonymousNotice:
          'Без входа платёж будет анонимным: он учтётся в фондах платформы, но не появится в истории вашего профиля.',
        checkoutAuthenticatedNotice:
          'Платёж будет сохранён в истории профиля вместе с trace ID и деталями маршрутизации.',
        checkoutLegalNotice:
          'Подтверждая, вы принимаете комиссию 10% и правила распределения средств для выбранного типа взноса.',
        checkoutMonthlyRequiresAuth:
          'Ежемесячный план можно оформить только после входа — он привязан к вашему профилю.',
        checkoutAuthSubtitle: 'Войдите, чтобы сохранить платёж в истории профиля.',
        checkoutAnonymousSuccess:
          'Анонимный платёж зарегистрирован. Он не отображается в истории профиля; сохраните trace ID из сообщения ниже.',
        checkoutAuthenticatedSuccess: 'Платёж зарегистрирован и сохранён в истории профиля.',
        checkoutRecurringSuccess: 'Ежемесячный план создан и доступен в профиле.',
        checkoutError: 'Не удалось зарегистрировать платеж. Проверьте параметры и повторите попытку.',
        checkoutTrace: 'Trace ID платежа: {traceId}',
        checkoutRecurringPlan: 'Создан recurring-план: {planId}',
        pay: {
          title: 'Оплата',
          subtitle: 'Укажите сумму и данные карты. Сейчас работает только тестовая seed-карта (все нули).',
          backToTiers: '← Выбор формата поддержки',
          sidebarTitle: 'Платёж',
          changeTierTitle: 'Сменить формат',
          routingTitle: 'Маршрутизация после комиссии 10%',
          methodLabel: 'Способ оплаты',
          methodCard: 'Банковская карта (заглушка)',
          seedCardTitle: 'Тестовая seed-карта',
          seedCardExpiry: 'Срок: {month}/{year}',
          seedCardCvv: 'CVV: {cvv}',
          cardNumberLabel: 'Номер карты',
          expiryMonthLabel: 'ММ',
          expiryYearLabel: 'ГГ',
          cvvLabel: 'CVV',
          holderLabel: 'Держатель карты',
        },
        routingPreviewTitle: 'Превью маршрутизации',
        tiers: {
          platform: {
            eyebrow: 'Платформа',
            name: 'Развитие LiveNet',
            description: 'Поддержка инфраструктуры, безопасности и публичной отчетности.',
            feature1: '100% net после комиссии — в фонд развития платформы',
            feature2: 'Разовый или ежемесячный платеж',
            feature3: 'Прозрачная трассировка платежа в личном кабинете',
            cta: 'Перейти к оплате',
          },
          common: {
            eyebrow: 'Сообщество',
            name: 'Общий взнос',
            description: 'Самый гибкий способ усилить общий фонд инициатив и распределение.',
            feature1: '100% net — в общий фонд инициатив',
            feature2: 'Подходит для регулярной поддержки экосистемы',
            feature3: 'Комиссия 10% отображается до подтверждения',
            cta: 'Перейти к оплате',
          },
          targeted: {
            eyebrow: 'Инициатива',
            name: 'Целевой взнос',
            description: 'Поддержка конкретной инициативы с понятным разделением средств.',
            feature1: '60% net — в бюджет инициативы, 40% — в общий фонд',
            feature2: 'Нужен ID инициативы перед оплатой',
            feature3: 'Маршрутизация видна в превью перед подтверждением',
            cta: 'Перейти к оплате',
          },
        },
        options: {
          one: {
            title: 'Разовый донат',
            body: 'Позже здесь появится выбор платежного провайдера для разовой финансовой поддержки. Пока это явная заглушка под будущую интеграцию.',
          },
          two: {
            title: 'Регулярная поддержка',
            body: 'Следующим шагом можно будет оформить регулярную поддержку продукта, чтобы команда могла стабильнее планировать развитие и сопровождение.',
          },
          three: {
            title: 'Партнерская помощь',
            body: 'Если вы хотите поддержать проект не только деньгами, но и ресурсами, инфраструктурой или экспертизой, сейчас лучше всего писать команде напрямую.',
          },
        },
        contactsTitle: 'Куда писать по вопросам донатов',
        contactsBody:
          'Вопросы о поддержке, партнерстве и способах оплаты можно адресовать команде через контакты ниже.',
        placeholderPayment: 'Платежный сервис скоро будет подключен.',
      },
      team: {
        eyebrow: 'Команда',
        title: 'Кто собирает LiveNet',
        subtitle:
          'Сейчас это небольшая рабочая группа, которая сводит вместе продуктовую логику, доверительные механики и инженерную дисциплину.',
        summary:
          'Мы строим систему так, чтобы каждое новое решение можно было объяснить, воспроизвести и проверить, а не держать на скрытых ручных договоренностях.',
        membersTitle: 'Кто над этим работает',
        members: {
          one: {
            role: 'Продукт и сценарии доверия',
            body: 'Эта часть команды следит за тем, чтобы делегирование оставалось понятным человеку без дополнительного технического перевода.',
          },
          two: {
            role: 'Инженерия и контракты',
            body: 'Здесь фокус на устойчивом backend/frontend поведении, предсказуемых API и постепенном устранении случайной сложности.',
          },
          three: {
            role: 'Контент, обратная связь и ревью',
            body: 'Проекту нужны люди, которые помогают задавать сильные вопросы, замечать шероховатости и улучшать продуктовую ясность до релиза.',
          },
        },
        contactsTitle: 'Как связаться с командой',
        contactsBody:
          'Если вы хотите обсудить сотрудничество, задать вопрос или помочь проекту, используйте один из текущих каналов связи ниже.',
      },
      platform: {
        eyebrow: 'Платформа',
        title: 'Как работает LiveNet',
        subtitle: 'Платформа помогает делегировать голос по цепочке доверия так, чтобы финальный получатель был виден и проверяем.',
        summary:
          'Сначала пользователь создает или восстанавливает сессию, затем выбирает человека, которому доверяет свой голос, а система показывает всю цепочку до финального получателя.',
        pointOne: 'Каждое делегирование проходит через валидируемые API-контракты и проверяемые backend правила.',
        pointTwo: 'Цепочка остается обозримой: можно увидеть корневого получателя и понять, кому в итоге достается голос.',
        pointThree: 'Интерфейс и PWA-оболочка строятся так, чтобы продуктом можно было пользоваться без лишнего трения и повторного входа.',
        primaryAction: 'Вернуться на главную',
        secondaryAction: 'Узнать о команде',
      },
    },
    homeConcepts: {
      common: {
        variantLabel: 'Вариант {number}',
        mockupTitle: 'Моковый экран',
        mockupHint:
          'Здесь намеренно нет реальной логики: только форма, ритм, плотность и ощущение интерфейса.',
        principlesTitle: 'Принцип дизайна',
        flowTitle: 'Путь пользователя',
        stepLabel: 'Шаг {number}',
      },
      shared: {
        title: 'Доверие превращается в действие',
        subtitle:
          'Домашняя страница объясняет LiveNet через один понятный сценарий: выберите человека, которому доверяете, и участвуйте в решениях без сложного входа.',
        primaryAction: 'Начать',
        secondaryAction: 'Как это работает',
        metricOneLabel: 'Главное действие',
        metricOneValue: 'делегировать',
        metricTwoLabel: 'Состояние',
        metricTwoValue: 'прозрачно',
        metricThreeLabel: 'Формат',
        metricThreeValue: 'PWA',
        pointOne: 'Один hero, одна карточка-превью, три метрики и два блока пояснения.',
        pointTwo: 'На всех вариантах структура одинаковая, чтобы сравнивать именно дизайн-систему.',
        pointThree: 'Меняются цветовая модель, радиусы, плотность, поверхности и поведение кнопок.',
        flowOne: 'Пользователь считывает обещание продукта.',
        flowTwo: 'Видит короткое превью будущего рабочего экрана.',
        flowThree: 'Выбирает первый шаг или открывает объяснение платформы.',
      },
      four: {
        eyebrow: 'Вариант 4 · signal dark',
      },
    },
    profile: {
      title: 'Мой профиль',
      loadingProfile: 'Загружаем профиль…',
      userNotFound: 'Профиль недоступен',
      userNotFoundHint: 'Чтобы открыть профиль, необходимо войти в систему. Если вы уже вошли — обновите страницу.',
      tabProfile: 'Профиль',
      tabProfileVerification: 'Подтверждение профиля',
      openProfileVerificationPage: 'Подтверждение профиля',
      openProfile: 'К профилю',
      profileVerificationPageSubtitle:
        'Проходите этапы подтверждения по шагам в профиле и возвращайтесь к остальным разделам в любой момент.',
      firstName: 'Имя',
      lastName: 'Фамилия',
      patronymic: 'Отчество',
      birthDate: 'Дата рождения',
      citizenship: 'Гражданство',
      kycEmail: 'Email для подтверждения',
      saveSuccess: 'Данные сохранены',
      saveFailed: 'Не удалось сохранить данные',
      statusIncomplete: 'Профиль не заполнен',
      statusFilled: 'Профиль заполнен',
      statusVerified: 'Профиль верифицирован',
      reputationLoadError: 'Не удалось загрузить метрики репутации.',
      analyticsLoadError: 'Не удалось загрузить аналитические агрегаты.',
      profileStatusTitle: 'Статус профиля',
      profileNeedsCompletion:
        'Укажите имя и email, затем подтвердите почту — это первый шаг к заполненному профилю.',
      profileFilledHint:
        'Обязательные данные внесены. Для статуса «верифицирован» загрузите документ и пройдите проверку лица.',
      funds: {
        title: 'Взносы и фонды',
        subtitle:
          'История ваших платежей, калькулятор маршрутизации, ежемесячные планы и снимок распределения общего фонда.',
        loginHint:
          'Войдите в аккаунт, чтобы видеть историю взносов, маршрутизацию и отчёт по распределению фонда. Разовый взнос без входа можно оформить на странице поддержки.',
        loadError: 'Не удалось загрузить данные по взносам.',
        exportError: 'Не удалось выгрузить CSV-выписку.',
        recurringUpdateError: 'Не удалось обновить статус ежемесячного плана.',
        routingTitle: 'Маршрутизация средств',
        routingHint:
          'После комиссии 10% net распределяется по типу взноса. Калькулятор помогает заранее увидеть разбивку.',
        routingTypeLabel: 'Тип взноса',
        routingAmountLabel: 'Сумма, ₽',
        routingFee: 'Комиссия: {amount} ₽',
        routingNet: 'Net после комиссии: {amount} ₽',
        routingPlatform: 'Фонд развития платформы: {amount} ₽',
        routingCommon: 'Общий фонд инициатив: {amount} ₽',
        routingInitiative: 'Бюджет инициативы: {amount} ₽',
        monthlySnapshotTitle: 'Месячный снимок распределения',
        monthlySnapshotEmpty: 'Снимок распределения за выбранный месяц пока недоступен.',
        monthLabel: 'Месяц: {month}',
        statusLabel: 'Статус: {status}',
        totalLabel: 'Общая сумма: {amount}',
        commissionLabel: 'Комиссия: {amount}',
        allocationsLabel: 'Аллокаций: {count}',
        fraudPassed: 'Антифрод-перепроверка пройдена',
        fraudFailed: 'Антифрод-перепроверка выявила ограничения',
        fraudPending: 'Антифрод-перепроверка ожидает завершения',
        platformFundTitle: 'Фонд развития платформы',
        platformFundAmount: 'За {month}: {amount}',
        platformFundEmpty: 'Снимок фонда развития пока недоступен.',
        recurringTitle: 'Ежемесячные планы',
        recurringEmpty: 'Активных или созданных планов пока нет.',
        recurringNextRun: 'Следующий запуск: {date}',
        recurringNotScheduled: 'не запланирован',
        recurringPause: 'Пауза',
        recurringResume: 'Возобновить',
        recurringCancel: 'Отменить',
        timelineTitle: 'События ежемесячных списаний',
        timelineEmpty: 'Событий пока нет.',
        timelineItem: 'План {planId} · период {period}',
        historyTitle: 'История платежей',
        historyEmpty: 'В истории профиля платежей пока нет.',
        exportCsv: 'Скачать CSV-выписку',
      },
      profileVerifiedHint:
        'Профиль полностью подтверждён: документ и верификация лица пройдены.',
      profileActionsTitle: 'Действия',
      profileActionsHint: 'Пройдите этапы по порядку, чтобы повысить уровень доверия и сделать профиль понятным другим участникам.',
      reputationTitle: 'Репутация',
      analyticsTitle: 'Аналитика',
      kycEmailStatus: 'Email подтверждён',
      kycEmailPending: 'Email не подтверждён. Проверьте почту.',
      kycEmailSent: 'Письмо отправлено на {email}. Перейдите по ссылке для подтверждения.',
      submitKycL1: 'Подтвердить',
      upgradeLevel: 'Повысить уровень доверия',
      editLevel: 'Изменить данные',
      kycL1SectionTitle: 'Шаг 1 — Профиль и контакты',
      kycL2SectionTitle: 'Шаг 2 — Документ',
      kycL3SectionTitle: 'Шаг 3 — Верификация лица',
      kycL1Hint:
        'Укажите имя и email. После подтверждения почты профиль получит статус «заполнен».',
      kycL2Hint:
        'Загрузите фото документа, удостоверяющего личность, чтобы перейти к проверке лица.',
      kycL3Hint: 'Пройдите верификацию лица, чтобы получить статус «верифицирован».',
      kycLevelLocked: 'Сначала завершите предыдущий шаг.',
      uploadDocumentL2: 'Загрузить документ',
      uploadDocumentL2Hint:
        'Загрузите чёткое фото или скан первой страницы документа (JPEG, PNG, WebP, до 10 МБ).',
      uploadSuccess: 'Документ проверен. Можно перейти к верификации лица.',
      startLivenessL3: 'Начать верификацию лица',
      livenessTitle: 'Верификация лица',
      livenessInstruction1: 'Держите паспорт рядом с лицом, чтобы оба были видны в кадре.',
      livenessInstruction2: 'Поверните голову влево, затем вправо.',
      livenessInstruction3: 'Посмотрите прямо в камеру.',
      livenessCapture: 'Сделать снимок',
      livenessSubmit: 'Отправить на проверку',
      livenessSuccess: 'Верификация лица пройдена. Профиль верифицирован.',
      livenessError: 'Ошибка верификации. Попробуйте ещё раз.',
      confirmEmailTitle: 'Подтверждение email',
      confirmEmailButton: 'Подтвердить email',
      confirmEmailSuccess: 'Email подтверждён. Профиль заполнен.',
      confirmEmailFailed: 'Не удалось подтвердить email.',
      confirmEmailMissingToken: 'Ссылка подтверждения недействительна. Запросите новое письмо.',
    },
    notFound: {
      title: '404 — Страница не найдена',
      subtitle: 'Этот маршрут не существует или уже был изменен. Основная навигация и контакты по-прежнему доступны в оболочке приложения.',
      summary: 'Вернитесь на рабочие страницы платформы или откройте раздел с описанием проекта, чтобы продолжить без тупика.',
      primaryAction: 'На главную',
      secondaryAction: 'О платформе',
    },
    chain: {
      title: 'Цепочка передачи',
      subtitle: 'Здесь видно путь от вас до человека, у которого голос остается в конце цепочки.',
      topNode: 'Вершина цепи',
      you: 'Это вы',
      empty: 'Цепочка пока не создана. Сначала передайте голос другому пользователю.',
      resolvedRootTitle: 'Финальный получатель',
      resolvedRootSubtitle: 'Это человек, у которого голос оказывается после всех передач по цепочке.',
      noRoot: 'Пока никому не передан',
      noRootSubtitle: 'Вы еще не настроили передачу голоса.',
      verifiedRoot: 'Аккаунт подтвержден',
      unverifiedRoot: 'Аккаунт не подтвержден',
      verifiedNode: 'Аккаунт подтвержден',
      unverifiedNode: 'Аккаунт не подтвержден',
      relatedGoals: 'Перейти к связанным инициативам',
    },
    common: {
      none: 'Нет',
      yes: 'Да',
      no: 'Нет',
      guestSession: 'Гостевой режим',
      loadingId: 'Загружаем ID...',
      close: 'Закрыть',
      cancel: 'Отмена',
      confirm: 'Подтвердить',
    },
    initiatives: {
      page: {
        title: 'Инициативы',
        subtitle: 'Ищите проекты сообщества, поддерживайте их или предложите свою.',
        searchLabel: 'Поиск по названию и описанию',
        empty: 'Инициатив пока нет.',
        emptySearch: 'По вашему запросу инициатив не найдено.',
        loadError: 'Не удалось загрузить инициативы. Попробуйте еще раз.',
      },
    },
    goals: {
      marketplace: {
        title: 'Маркетплейс инициатив',
        subtitle: 'Каталог инициатив для поиска, поддержки и участия.',
        create: 'Создать инициативу',
        scopeAll: 'Все инициативы',
        scopeMine: 'Мои инициативы',
        searchLabel: 'Поиск по инициативам',
        searchText: 'Текстовый поиск',
        searchSemantic: 'Semantic search',
        statusLabel: 'Статус',
        statusAll: 'Все',
        statusActive: 'Активные',
        statusArchived: 'Архив',
        sortLabel: 'Сортировка',
        sortNewest: 'Сначала новые',
        sortOldest: 'Сначала старые',
        sortTasks: 'Больше задач',
        semanticHint: 'Семантический режим ранжирует инициативы по смыслу запроса (без LLM-чата).',
        signInPrompt: 'Чтобы создать инициативу,',
        signInLink: 'войдите в аккаунт',
        relevance: 'релевантность {percent}%',
        meta: 'Статус: {status} · Задач: {tasks}',
        emptySemantic: 'По смыслу запроса ничего не найдено.',
        empty: 'Инициатив пока нет.',
        loadError: 'Не удалось загрузить маркетплейс инициатив.',
        mineRequiresAuth: 'Для раздела «Мои инициативы» требуется вход в аккаунт.',
      },
      create: {
        title: 'Создание инициативы',
        subtitle: 'Пока вы заполняете текст, мы ищем похожие инициативы по смыслу — чтобы избежать дублей.',
        back: '← К маркетплейсу',
        toMarketplace: 'К маркетплейсу',
        authRequired: 'Войдите в аккаунт, чтобы предложить новую инициативу.',
        titleLabel: 'Название',
        descriptionLabel: 'Описание',
        descriptionHelper: 'Минимум 10 символов',
        similarTitle: 'Похожие инициативы',
        similarHint: 'Заполните название и описание — покажем похожие проекты.',
        similarEmpty: 'Похожих инициатив не найдено — можно создавать новую.',
        similarity: 'сходство {percent}',
        submit: 'Создать инициативу',
        createError: 'Не удалось создать инициативу.',
      },
    },
    errors: errorMessages,
  },
} as const;

export type Locale = keyof typeof messages;
export type MessageTree = (typeof messages)[Locale];
export type MessageKey = NestedKeyOf<MessageTree>;

type NestedKeyOf<T> = {
  [K in keyof T & string]: T[K] extends string ? K : `${K}.${NestedKeyOf<T[K]>}`;
}[keyof T & string];
